import TitleSeparation from "components/shared/TitleSeparation";
import ChannelsRecap from "components/TermRecap/ChannelsRecap";
import { groupChannelFormulas } from "components/TermRecap/formatTypeRecap";
import GroupRecap from "components/TermRecap/GroupRecap";
import { getTemplateChannels } from "components/TermTemplates/utils";
import { PortalType } from "contexts/portal.context";
import type { GroupParams } from "containers/TermFormContainer";
import { getGroupWithChannels } from "containers/TermFormContainer/utils";
import { formatNumber } from "helpers/utils";
import { useCompanySettings } from "hooks/useCompanySettings";
import { useContext, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { fetchChannelCompensationGroups } from "store/channelStore";
import { useAppDispatch, useAppSelector } from "store/store";
import type { PressTemplateConfiguration, TemplateConfiguration } from "store/termTemplatesStore";
import { fetchTitleFormatTypes } from "store/titleFormatTypeStore";

import styles from "../style.module.scss";

interface ITemplateDetails {
    /**
     * Settings of the template (advancePayment, channels)
     */
    templateConfiguration: TemplateConfiguration;
    /**
     * Triggers deletion of template.
     */
    handleDelete: () => void;
}

// Within Term Templates, the details of the template that open when clicking on Template Block:
const TemplateDetails = ({ templateConfiguration }: ITemplateDetails) => {
    const [t] = useTranslation();
    const dispatch = useAppDispatch();
    const portalType = useContext(PortalType);

    const { getCompanySettings } = useCompanySettings();
    const usesGroups = getCompanySettings("uses_interchannel_compensation");

    const { channels, channelCompensationGroups } = useAppSelector((state) => state.channels);
    const { items: titleFormatTypes, status: titleFormatTypesStatus } = useAppSelector(
        (state) => state.titleFormatTypes,
    );

    useEffect(() => {
        dispatch(fetchChannelCompensationGroups());
        if (portalType === "press" || portalType === "audiovisual") return;
        if (titleFormatTypesStatus === "idle") dispatch(fetchTitleFormatTypes());
    }, []);

    const channelById = useMemo(() => new Map(channels.map((channel) => [channel.id, channel])), [channels]);
    const formatTypeNamesById = useMemo(
        () => new Map(titleFormatTypes.map((item) => [item.id, item.name])),
        [titleFormatTypes],
    );

    if (portalType === "press" || portalType === "audiovisual") {
        const pressConfig = templateConfiguration as unknown as PressTemplateConfiguration;
        return (
            <div className={styles["template-details-wrapper"]}>
                <TitleSeparation
                    title={t("TermTemplates.PressConfiguration")}
                    titleStyle={{ fontSize: "14px", color: "rgb(100, 100, 100)" }}
                />
                <div className={styles["advance-details"]}>
                    <div>
                        <span className={styles["grey"]}>{t("TermTemplates.PressFolioPrice")} </span>
                        <span className={styles["option-text"]}>
                            {formatNumber((pressConfig?.folioPrice ?? 0) / 100, {
                                isCurrency: true,
                                always2Digit: true,
                            })}
                        </span>
                    </div>
                    <div>
                        <span className={styles["grey"]}>{t("TermTemplates.PressWorkNature")} </span>
                        <span className={styles["option-text"]}>{pressConfig?.workNature || "—"}</span>
                    </div>
                </div>
            </div>
        );
    }

    const configurationChannels =
        getTemplateChannels(templateConfiguration, channels, { usesCompensation: !!usesGroups }) ?? [];
    const standaloneRows = usesGroups
        ? configurationChannels.filter((channel) => channel.groupId == null)
        : configurationChannels;
    const standaloneFormulas = groupChannelFormulas(standaloneRows);
    const groupsWithChannels = getGroupWithChannels(channelCompensationGroups, configurationChannels);

    return (
        <div className={styles["template-details-wrapper"]}>
            <TitleSeparation
                title={t("TermTemplates.AdvanceSettings")}
                titleStyle={{ fontSize: "14px", color: "rgb(100, 100, 100)" }}
            />
            <div className={styles["advance-details"]}>
                <div>
                    <span className={styles["grey"]}>{t("TermRecap.Advance")} </span>
                    <span className={styles["option-text"]} id="recap-advance-payment">
                        {templateConfiguration.advancePayment?.toString() === ""
                            ? "0.00 €"
                            : formatNumber(templateConfiguration.advancePayment / 100, {
                                  isCurrency: true,
                                  always2Digit: true,
                              }) ?? "0"}
                    </span>
                </div>
                <div>
                    <span className={styles["grey"]}>
                        {t("ConnectTitle.Compensate")}{" "}
                        <span className={styles["option-text"]} id="recap-compensate">
                            {+`${templateConfiguration.compensate}` === 0
                                ? t("ConnectTitle.No")
                                : t("ConnectTitle.Yes")}
                        </span>
                    </span>
                </div>
            </div>

            {standaloneFormulas.size > 0 && (
                <TitleSeparation
                    title={t("TermTemplates.ChannelsPercentages")}
                    titleStyle={{ fontSize: "14px", color: "rgb(100, 100, 100)" }}
                />
            )}
            {[...standaloneFormulas.entries()].map(([channelId, formulas]) => (
                <ChannelsRecap
                    key={channelId}
                    channel={formulas.defaultRow}
                    formatOverrides={formulas.overrides}
                    fullChannelData={channelById.get(channelId)}
                    formatTypeNamesById={formatTypeNamesById}
                />
            ))}
            {usesGroups &&
                groupsWithChannels &&
                groupsWithChannels.map((group: GroupParams) => {
                    const groupChannels = configurationChannels.filter((channel) => channel.groupId === group.id);
                    const defaultChannelIds = new Set(
                        groupChannels
                            .filter((channel) => channel.formatTypeId == null)
                            .map((channel) => channel.channelId),
                    );
                    const nameChannelIds =
                        defaultChannelIds.size > 0
                            ? defaultChannelIds
                            : new Set(groupChannels.map((channel) => channel.channelId));
                    const groupChannelsNames = channels
                        .filter((channel) => nameChannelIds.has(channel.id))
                        .map((channel) => channel.name);

                    return (
                        <GroupRecap
                            key={group.id}
                            group={group}
                            groupChannels={groupChannels}
                            groupChannelsNames={groupChannelsNames}
                            channels={channels}
                            formatTypeNamesById={formatTypeNamesById}
                        />
                    );
                })}
        </div>
    );
};

export default TemplateDetails;
